import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { createMaterialLibrary } from '../scene/materials';
import { makeCustomDoor, disposeModel } from '../configurator/model';
import { addConceptArchitecture } from './conceptArchitecture';
import { addGlassBackdrop, houseFraming, HOUSE_SCENES } from './glassBackdrop';

export async function renderStudioReference(design, product, finish, scene = 'scenery') {
  if (!product || !finish) throw new Error('Choose a Studio product and finish first.');
  const house = HOUSE_SCENES.includes(scene);
  let renderer, library, model, environment, pmrem, room, disposeBackdrop;
  const measures = [];
  try {
    renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    const gl = renderer.getContext(), info = gl.getExtension('WEBGL_debug_renderer_info');
    renderer.userData = { software: !info || /swiftshader|llvmpipe|software/i.test(gl.getParameter(info.UNMASKED_RENDERER_WEBGL)) };
    renderer.setSize(1024, 1024); renderer.setPixelRatio(1);
    renderer.outputColorSpace = THREE.SRGBColorSpace; renderer.toneMapping = THREE.ACESFilmicToneMapping; renderer.toneMappingExposure = 1.15;
    renderer.shadowMap.enabled = !renderer.userData.software; renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    const scene3d = new THREE.Scene(); renderer.setClearColor(0x000000, 0);
    pmrem = new THREE.PMREMGenerator(renderer); room = new RoomEnvironment(); environment = pmrem.fromScene(room, .04); scene3d.environment = environment.texture; scene3d.environmentIntensity = house ? .3 : .9;
    library = createMaterialLibrary(renderer, () => {}); library.setPhotoMode(true);
    library.wall.color.set('#dfe4dd');
    const frame = library.makeFrame(); library.applyFinish(frame, finish);
    model = makeCustomDoor(design, product, library, frame);
    model.runs.forEach(run => { if (run.measures) { run.measures.removeFromParent(); measures.push(run.measures); } });
    model.group.position.y = product.is_fixed ? .8 : 0;
    const bounds = new THREE.Box3().setFromObject(model.group), center = bounds.getCenter(new THREE.Vector3()), size = bounds.getSize(new THREE.Vector3());
    if (house) disposeBackdrop = addGlassBackdrop(model, design, !!product.is_fixed, scene); else if (scene !== 'plain') addConceptArchitecture(model, design, !!product.is_fixed, library);
    scene3d.add(model.group);
    scene3d.add(new THREE.HemisphereLight('#ffffff', '#b7c1ba', house ? .65 : 1.8));
    const sun = new THREE.DirectionalLight('#fff7e9', house ? 1.4 : 2.6); sun.position.copy(center).add(new THREE.Vector3(-3, 5, 5)); sun.castShadow = !renderer.userData.software;
    sun.shadow.mapSize.set(2048, 2048); scene3d.add(sun);
    const fill = new THREE.DirectionalLight('#e4efff', .9); fill.position.copy(center).add(new THREE.Vector3(4, 1, 3)); scene3d.add(fill);
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, .01, 200);
    const direction = design.layout === 'corner' ? new THREE.Vector3(1.3, .22, 1.3) : new THREE.Vector3(0, .01, 1);
    camera.position.copy(center).add(direction.normalize().multiplyScalar(Math.max(size.length() * 3, 6))); camera.lookAt(center); camera.updateMatrixWorld(true);
    const projected = new THREE.Box3();
    for (const x of [bounds.min.x, bounds.max.x]) for (const y of [bounds.min.y, bounds.max.y]) for (const z of [bounds.min.z, bounds.max.z]) projected.expandByPoint(new THREE.Vector3(x, y, z).applyMatrix4(camera.matrixWorldInverse));
    const span = projected.getSize(new THREE.Vector3());
    if (house) {
      const { frame: extent, centerFromTop } = houseFraming(!!product.is_fixed, span);
      camera.left = -extent / 2; camera.right = extent / 2; camera.top = centerFromTop * extent; camera.bottom = -(1 - centerFromTop) * extent;
    } else {
      const half = Math.max(span.x, span.y) * .66;
      camera.left = -half; camera.right = half; camera.top = half; camera.bottom = -half;
    }
    camera.updateProjectionMatrix();
    const start = performance.now();
    while (library.status === 'loading' && performance.now() - start < 15000) await new Promise(resolve => setTimeout(resolve, 50));
    if (library.status !== 'ready') throw new Error('Studio materials could not load. Please try again before generating.');
    renderer.render(scene3d, camera);
    const blob = await new Promise(resolve => renderer.domElement.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('The exact Studio model could not be captured. Please reopen Studio.');
    return { blob, snapshot: { ...design, photo_overlay: null } };
  } catch (error) {
    throw new Error(error.message || 'The exact Studio preview could not be prepared.');
  } finally {
    if (model) disposeModel(model.group);
    disposeBackdrop?.(); measures.forEach(disposeModel); library?.dispose(); environment?.dispose(); pmrem?.dispose(); room?.dispose(); renderer?.dispose(); renderer?.forceContextLoss();
  }
}